import { memo, useEffect, useState } from "react";
import skyBand from "../assets/img/sky-band.svg";
import planeImg from "../assets/img/plane.svg";
import golinksLogo from "../assets/img/logos/golinks.svg";
import gosearchLogo from "../assets/img/logos/gosearch.svg";
import firstupLogo from "../assets/img/logos/firstup.svg";
import appleLogo from "../assets/img/logos/apple.svg";
import infosysLogo from "../assets/img/logos/infosys.png";
import { projects, projectById } from "../data/projects";
import { CaseStudy } from "./CaseStudy";

const logos = [
  { name: "GoLinks", src: golinksLogo },
  { name: "GoSearch", src: gosearchLogo },
  { name: "Firstup", src: firstupLogo },
  { name: "Apple", src: appleLogo },
  { name: "Infosys", src: infosysLogo },
];

const stack = [
  {
    n: "01",
    title: "The bet",
    body: "I decide what is in, and what I will not ship. GoSearch does not launch an answer it cannot cite.",
    work: "gosearch",
  },
  {
    n: "02",
    title: "The system",
    body: "The integrations page is part of the system. Indexed, federated, and MCP, plus GoSearch itself inside Claude.",
    work: "gosearch-mcp",
  },
  {
    n: "03",
    title: "The product",
    body: "Web, iPhone, iPad, Android. 4.0 shipped on 14 October 2026 because I built it. There was no team to wait for.",
    work: "gosearch-apps",
  },
  {
    n: "04",
    title: "The business",
    body: "Predictive apps, no dedicated engineering team, $1M+ of operating cost out. Same standard at GoSearch: freemium that put $1M+ on the line.",
    work: "analytics",
  },
];

const fieldCards = [
  { id: "clients", value: "250+", note: "clients onboarded", meta: "Firstup", work: "data-platform", tilt: "-2.4deg" },
  { id: "cost", value: "$1M+", note: "operating cost out", meta: "Analytics", work: "analytics", tilt: "1.8deg" },
  { id: "search", value: "GoSearch", note: "AI retrieval", meta: "Now shipping", work: "gosearch", tilt: "-0.6deg", featured: true },
  { id: "apps", value: "2 apps", note: "iOS and Android", meta: "Shipped solo", work: "gosearch-apps", tilt: "1.4deg" },
  { id: "accuracy", value: "30%", note: "accuracy lift", meta: "GoSearch AI", work: "gosearch", tilt: "2.2deg" },
  { id: "apple", value: "Apple", note: "online store", meta: "Engagement", work: "apple", tilt: "-1.6deg" },
];

const StoryField = ({ onOpen }) => (
  <div className="story-field">
    <p className="story-field-kicker">
      <span className="kicker-dot" />
      From the field
    </p>
    <div className="story-cards">
      {fieldCards.map((card, index) => (
        <button
          className={`story-card${card.featured ? " is-featured" : ""}`}
          key={card.id}
          type="button"
          style={{ "--tilt": card.tilt, animationDelay: `${index * 80}ms` }}
          onClick={() => onOpen(card.work)}
          aria-label={`Open the ${projectById[card.work]?.title || card.note} case study`}
        >
          <span className="story-card-pin" aria-hidden="true" />
          <span className="story-card-value">{card.value}</span>
          <span className="story-card-note">{card.note}</span>
          <span className="story-card-meta">{card.meta}</span>
        </button>
      ))}
    </div>
  </div>
);

const planePath =
  "M 180 80 C 90 180, 70 300, 160 380 C 280 480, 720 420, 1180 520 C 1360 560, 1360 760, 1100 840 C 820 930, 420 860, 180 980 C 40 1080, 80 1280, 360 1320 C 760 1380, 1280 1280, 1320 1520 C 1360 1760, 900 1780, 520 1720 C 220 1670, 140 1880, 360 1980 C 640 2100, 1180 2040, 1240 2280";

const hashId = () => {
  const match = window.location.hash.match(/^#work\/([a-z0-9-]+)/i);
  return match ? match[1] : null;
};

export const Projects = memo(function Projects() {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const apply = () => {
      const id = hashId();
      setOpenId(projects.some((item) => item.id === id) ? id : null);
    };
    apply();
    window.addEventListener("hashchange", apply);
    window.addEventListener("popstate", apply);
    return () => {
      window.removeEventListener("hashchange", apply);
      window.removeEventListener("popstate", apply);
    };
  }, []);

  const open = (id) => {
    window.history.pushState(null, "", `#work/${id}`);
    setOpenId(id);
  };

  const close = () => {
    window.history.pushState(null, "", "#work");
    setOpenId(null);
  };

  return (
    <>
      <section className="projects" id="story">
        <div className="stack-lid" aria-hidden="true" />
        <div className="sky-band" style={{ backgroundImage: `url(${skyBand})` }} aria-hidden="true" />
        <StoryField onOpen={open} />
        <div className="intro-copy">
          <p className="intro-type">
            I’m a full-stack product manager in Seattle. I lead strategy and technical
            decisions together: what to bet on, how the system is built, and when it is
            ready to ship. I stay in the work, and I hold it to a result the business
            can stand behind.
          </p>
          <p className="bold intro-type">
            I don’t ship a model people clap for in a demo. The product ships, and
            the number has to move.
          </p>
        </div>

        <div className="pm-stack">
          <p className="pm-stack-kicker">
            <span className="kicker-dot" />
            Full stack, zero to one
          </p>
          <h2>I own it from the blank page to the number.</h2>
          <p className="pm-stack-lede">
            A deck is not a product. I set the bet, I hold the bar, and if the team for a layer
            does not exist, I build that layer.
          </p>
          <ol className="pm-steps">
            {stack.map((step, index) => (
              <li key={step.n}>
                <button
                  className="pm-step"
                  type="button"
                  style={{ animationDelay: `${index * 70}ms` }}
                  onClick={() => open(step.work)}
                  aria-label={`Open the ${projectById[step.work]?.title || step.title} case study`}
                >
                  <span className="pm-step-index">{step.n}</span>
                  <strong>{step.title}</strong>
                  <p>{step.body}</p>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="featured-work" id="work">
        <div className="plane-fly" aria-hidden="true">
          <svg className="plane-trail" viewBox="0 0 1440 2400" fill="none" preserveAspectRatio="none">
            <defs>
              <mask id="planeTrailMask">
                <path
                  id="planeTrailMaskPath"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="44"
                  d={planePath}
                />
              </mask>
            </defs>
            <path id="planeMotionPath" fill="none" stroke="none" d={planePath} />
            <path
              id="planeTrailPath"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeDasharray="10 6"
              mask="url(#planeTrailMask)"
              d={planePath}
            />
          </svg>
          <img className="plane-sprite" src={planeImg} alt="" />
        </div>

        <h2 className="featured-heading reveal">Work featured at</h2>

        <div className="logo-marquee-wrap" aria-label="Companies">
          <div className="logo-marquee-inner">
            {[...logos, ...logos].map((logo, i) => (
              <span className={`client-logo logo-${logo.name.toLowerCase()}`} key={`${logo.name}-${i}`}>
                <img src={logo.src} alt={logo.name} />
              </span>
            ))}
          </div>
        </div>

        <div className="project-tiles">
          {[
            ["gosearch", "gosearch-apps"],
            ["gosearch-mcp", "data-platform"],
            ["analytics", "apple"],
          ].map((ids) => (
            <div className={`tiles-row${ids.length > 2 ? " tiles-row--even" : ""}`} key={ids.join("-")}>
              {ids.map((id) => {
                const item = projectById[id];
                return (
                  <button
                    className={`project-tile tile-${item.size} tone-${item.tone} fit-${item.fit}`}
                    key={item.id}
                    type="button"
                    aria-label={`Open the ${item.title} case study`}
                    onClick={() => open(item.id)}
                  >
                    <img src={item.img} alt="" />
                    <div className="tile-overlay">
                      <h3 className="tile-overlay-title">{item.title}</h3>
                      <p className="tile-overlay-subtitle">{item.subtitle}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {openId && <CaseStudy projectId={openId} onClose={close} onOpen={open} />}
    </>
  );
});
